import React from 'react'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Pair, TokenAmount, Currency, JSBI } from 'vvs-sdk'
import { Text, Flex, Heading, PairDepositStatsIcon, useMatchBreakpoints } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import { unwrappedToken } from 'utils/wrappedCurrency'
import useTheme from 'hooks/useTheme'
import useTotalSupply from 'hooks/useTotalSupply'
import { space, SpaceProps } from 'styled-system'

export interface PairStatsProps {
  pair: Pair
  stakedLPAmount: BigNumber
  token0Supplied: BigNumber
  token1Supplied: BigNumber
}

const Wrapper = styled.div`
  margin-top: 24px;
`

const Row = styled.div<SpaceProps>`
  display: grid;
  grid-template-columns: 2fr repeat(2, 1fr);

  ${space}
`

const PairStats: React.FC<PairStatsProps> = ({ pair, stakedLPAmount, token0Supplied, token1Supplied }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { isMobile } = useMatchBreakpoints()

  // unwrapped pair tokens
  const token0: Currency = unwrappedToken(pair.token0)
  const token1: Currency = unwrappedToken(pair.token1)

  const totalPoolTokens: TokenAmount = useTotalSupply(pair.liquidityToken)

  const parsedStakedLiquidity = new TokenAmount(pair.liquidityToken, stakedLPAmount.toString())

  // current tokens worth
  const token0Current =
    pair &&
    totalPoolTokens &&
    parsedStakedLiquidity &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, parsedStakedLiquidity.raw)
      ? new TokenAmount(
          pair.token0,
          pair.getLiquidityValue(pair.token0, totalPoolTokens, parsedStakedLiquidity, false).raw,
        )
      : undefined
  const token1Current =
    pair &&
    totalPoolTokens &&
    parsedStakedLiquidity &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, parsedStakedLiquidity.raw)
      ? new TokenAmount(
          pair.token1,
          pair.getLiquidityValue(pair.token1, totalPoolTokens, parsedStakedLiquidity, false).raw,
        )
      : undefined

  // diff
  const parsedToken0 = new TokenAmount(pair.token0, token0Supplied.toFixed(0))
  const parsedToken1 = new TokenAmount(pair.token1, token1Supplied.toFixed(0))
  const isToken0ChangeNegative = token0Current && parsedToken0?.greaterThan(token0Current)
  const isToken1ChangeNegative = token1Current && parsedToken1?.greaterThan(token1Current)
  const token0Diff = !isToken0ChangeNegative
    ? token0Current?.subtract(parsedToken0)
    : parsedToken0?.subtract(token0Current)
  const token1Diff = !isToken1ChangeNegative
    ? token1Current?.subtract(parsedToken1)
    : parsedToken1?.subtract(token1Current)

  const formatedAmounts = {
    token0Current: token0Current ? token0Current.toSignificant() : '',
    token1Current: token1Current ? token1Current.toSignificant() : '',
  }

  return (
    <Wrapper>
      <Flex mt="20px" mb="20px">
        <PairDepositStatsIcon mr="8px" />
        <Heading>{t('Pair Deposit Stats')}</Heading>
      </Flex>
      {!isMobile && (
        <>
          <Row my="16px">
            <Flex />
            <Text>{token0.symbol}</Text>
            <Text>{token1.symbol}</Text>
          </Row>
          <Row my="16px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Deposited Tokens')}
              </Text>
            </Flex>
            <Text>{pair && new TokenAmount(pair.token0, token0Supplied.toFixed(0)).toSignificant()}</Text>
            <Text ml="0.5rem">{pair && new TokenAmount(pair.token1, token1Supplied.toFixed(0)).toSignificant()}</Text>
          </Row>
          <Row my="16px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Token Change')}
              </Text>
            </Flex>

            {totalPoolTokens ? (
              <Text color={isToken0ChangeNegative ? theme.colors.failure : theme.colors.success}>
                {isToken0ChangeNegative ? '-' : ''}
                {token0Diff?.toSignificant()}
              </Text>
            ) : (
              <Text />
            )}
            {totalPoolTokens ? (
              <Text ml="0.5rem" color={isToken1ChangeNegative ? theme.colors.failure : theme.colors.success}>
                {isToken1ChangeNegative ? '-' : ''}
                {token1Diff?.toSignificant()}
              </Text>
            ) : (
              <Text />
            )}
          </Row>
          <Row my="16px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Current Tokens')}
              </Text>
            </Flex>
            {totalPoolTokens ? <Text>{formatedAmounts.token0Current}</Text> : <Text />}
            {totalPoolTokens ? <Text ml="0.5rem">{formatedAmounts.token1Current}</Text> : <Text />}
          </Row>
        </>
      )}
      {isMobile && (
        <>
          <Row my="16px">
            <Flex />
            <Text>{token0.symbol}</Text>
          </Row>
          <Row my="16px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Deposited Tokens')}
              </Text>
            </Flex>
            <Text>{pair && new TokenAmount(pair.token0, token0Supplied.toFixed(0)).toSignificant()}</Text>
          </Row>
          <Row my="16px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Token Change')}
              </Text>
            </Flex>

            {totalPoolTokens ? (
              <Text color={isToken0ChangeNegative ? theme.colors.failure : theme.colors.success}>
                {isToken0ChangeNegative ? '-' : ''}
                {token0Diff?.toSignificant()}
              </Text>
            ) : (
              <Text />
            )}
          </Row>
          <Row mt="16px" mb="52px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Current Tokens')}
              </Text>
            </Flex>
            {totalPoolTokens ? <Text>{formatedAmounts.token0Current}</Text> : <Text />}
          </Row>

          <Row my="16px">
            <Flex />
            <Text>{token1.symbol}</Text>
          </Row>
          <Row my="16px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Deposited Tokens')}
              </Text>
            </Flex>
            <Text>{pair && new TokenAmount(pair.token1, token1Supplied.toFixed(0)).toSignificant()}</Text>
          </Row>
          <Row my="16px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Token Change')}
              </Text>
            </Flex>

            {totalPoolTokens ? (
              <Text color={isToken1ChangeNegative ? theme.colors.failure : theme.colors.success}>
                {isToken1ChangeNegative ? '-' : ''}
                {token1Diff?.toSignificant()}
              </Text>
            ) : (
              <Text />
            )}
          </Row>
          <Row my="16px">
            <Flex mt="3px">
              <Text color="textSubtle" fontSize="13px">
                {t('Current Tokens')}
              </Text>
            </Flex>
            {totalPoolTokens ? <Text>{formatedAmounts.token1Current}</Text> : <Text />}
          </Row>
        </>
      )}
    </Wrapper>
  )
}

export default PairStats
